import { useEffect, useState } from "react";
import { api } from "./api";
import type { AdminUser } from "./types";

export function AdminPanel({ meId, onClose }: { meId: string; onClose: () => void }) {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    try { setUsers((await api.adminUsers()).users); } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function act(id: string, action: "approve" | "reject") {
    setBusyId(id);
    try { action === "approve" ? await api.approveUser(id) : await api.rejectUser(id); await load(); }
    finally { setBusyId(null); }
  }

  async function changeRole(u: AdminUser, role: "member" | "producer" | "admin") {
    setBusyId(u.id);
    try { await api.setUserRole(u.id, role); await load(); }
    catch { /* backend blocks self-demotion */ }
    finally { setBusyId(null); }
  }

  async function remove(u: AdminUser) {
    if (!confirm(`${u.name || u.email} wirklich entfernen? Alle Boards & Daten dieses Users werden gelöscht.`)) return;
    setBusyId(u.id);
    try { await api.deleteUser(u.id); await load(); }
    finally { setBusyId(null); }
  }

  const pending = users.filter((u) => u.approval_status === "pending");
  const others = users.filter((u) => u.approval_status !== "pending");

  const badge = (s: string) => {
    const c = s === "approved" ? "var(--green)" : s === "rejected" ? "var(--danger)" : "var(--accent)";
    const t = s === "approved" ? "Freigegeben" : s === "rejected" ? "Abgelehnt" : "Wartet";
    return <span className="tag" style={{ background: c }}>{t}</span>;
  };

  return (
    <div className="overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 620 }} onClick={(e) => e.stopPropagation()}>
        <h2>Admin · Benutzerfreigabe</h2>
        {loading ? <div className="spin" /> : (
          <>
            <h3 style={{ fontSize: 13, color: "var(--text-dim)", margin: "10px 0 6px" }}>
              Warteschlange ({pending.length})
            </h3>
            {!pending.length && <p className="muted">Keine offenen Anfragen.</p>}
            {pending.map((u) => (
              <div className="row-flex" key={u.id} style={{ justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid var(--border)" }}>
                <div><strong>{u.name || u.email}</strong><div className="muted">{u.email}</div></div>
                <div className="row-flex">
                  <button className="btn btn-sm" disabled={busyId === u.id} onClick={() => act(u.id, "approve")}>Freigeben</button>
                  <button className="btn btn-danger btn-sm" disabled={busyId === u.id} onClick={() => act(u.id, "reject")}>Ablehnen</button>
                </div>
              </div>
            ))}

            <h3 style={{ fontSize: 13, color: "var(--text-dim)", margin: "18px 0 6px" }}>
              Alle Benutzer ({others.length})
            </h3>
            {others.map((u) => (
              <div className="row-flex" key={u.id} style={{ justifyContent: "space-between", padding: "7px 0", borderBottom: "1px solid var(--border)", gap: 8 }}>
                <div style={{ minWidth: 0 }}><strong>{u.name || u.email}</strong><div className="muted" style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{u.email} · 2FA {u.totp_enabled ? "✓" : "—"}</div></div>
                <div className="row-flex" style={{ flexShrink: 0 }}>
                  {badge(u.approval_status)}
                  {u.approval_status === "rejected" && (
                    <button className="btn btn-sm" disabled={busyId === u.id} onClick={() => act(u.id, "approve")}>Freigeben</button>
                  )}
                  {u.id !== meId ? (
                    <>
                      <select className="input" style={{ width: "auto", padding: "5px 8px" }} value={u.role ?? "member"}
                        disabled={busyId === u.id}
                        onChange={(e) => changeRole(u, e.target.value as "member" | "producer" | "admin")}>
                        <option value="member">Mitarbeiter</option>
                        <option value="producer">Producer</option>
                        <option value="admin">Admin</option>
                      </select>
                      <button className="btn btn-danger btn-sm" disabled={busyId === u.id} onClick={() => remove(u)}>Entfernen</button>
                    </>
                  ) : (
                    <span className="tag" style={{ background: "var(--accent-2)" }}>Du · Admin</span>
                  )}
                </div>
              </div>
            ))}
          </>
        )}
        <div style={{ marginTop: 18, textAlign: "right" }}>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>Schließen</button>
        </div>
      </div>
    </div>
  );
}
